import { Rule } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import FailedIcon from "shared/icons/status-icons/fail-icon.svg?react";
import { DecisionAction } from "shared/types";
import { Applicant } from "types/application";
import { RuleConditionRow } from "./RuleConditionRow";
import { HugeiconsIcon } from "@hugeicons/react";
import { CancelCircleIcon } from "@hugeicons-pro/core-solid-rounded";

const SummaryHeaderIcon = ({
  recommendation
}: {
  recommendation: DecisionAction;
}) => {
  if (recommendation === DecisionAction.Approve)
    return <SuccessIcon className="w-5 shrink-0 text-success-500" />;
  if (recommendation === DecisionAction.Decline)
    return <FailedIcon className="w-5 shrink-0 text-danger-500" />;
  return <FailedIcon className="w-5 shrink-0 text-warning-500" />;
};

const ConditionalApprovalNotesLine = ({ text }: { text: string }) => {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-dashed border-alpha-100 px-4 py-3 bg-background-900 h-[52px]">
      <HugeiconsIcon
        icon={CancelCircleIcon}
        size={20}
        className="text-content-100"
      />
      <p className="text-sm text-content-1000 font-bold">{text}</p>
    </div>
  );
};

const ConditionalApprovalNotes = ({
  isMetDeclineConditions,
  isMetApprovalConditions
}: {
  isMetDeclineConditions: boolean;
  isMetApprovalConditions: boolean;
}) => {
  return (
    <>
      <ConditionalApprovalNotesLine
        text={
          isMetDeclineConditions
            ? "Some decline conditions were met"
            : "No decline conditions met"
        }
      />

      <ConditionalApprovalNotesLine
        text={
          isMetApprovalConditions
            ? "Some approval conditions were met"
            : "No approval conditions met"
        }
      />
    </>
  );
};

interface SummaryConditionsProps {
  subtitle: string;
  currentRecommendation: DecisionAction;
  summaryRules: Rule[];
  applicants: Applicant[];
  isMetDeclineConditions: boolean;
  isMetApprovalConditions: boolean;
}

export const SummaryConditions = ({
  subtitle,
  currentRecommendation,
  summaryRules,
  applicants,
  isMetDeclineConditions,
  isMetApprovalConditions
}: SummaryConditionsProps) => {
  const isDecline = currentRecommendation === DecisionAction.Decline;
  const isConditional =
    currentRecommendation === DecisionAction.ConditionalApprove;

  return (
    <Card className="mt-4">
      <div className="px-6 py-4 border-b border-alpha-40 flex items-center gap-2">
        <SummaryHeaderIcon recommendation={currentRecommendation} />
        <h2 className="text-base font-bold text-content-1000">{subtitle}</h2>
      </div>

      <div className="p-4 space-y-3">
        <Show if={isConditional}>
          <ConditionalApprovalNotes
            isMetDeclineConditions={isMetDeclineConditions}
            isMetApprovalConditions={isMetApprovalConditions}
          />
        </Show>

        <Show if={!isConditional}>
          {summaryRules.length ? (
            summaryRules.map((rule, index) => (
              <RuleConditionRow
                key={`${rule.field_name}-${index}`}
                rule={rule}
                applicants={applicants}
                showStatusBadge
                isDecline={isDecline}
              />
            ))
          ) : (
            <p className="text-sm text-content-700">
              No rule-level details are available for this recommendation.
            </p>
          )}
        </Show>
      </div>
    </Card>
  );
};
